import { ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Output, ViewChild, OnDestroy } from '@angular/core';
import { Subscription, Observable, expand, throwError, delayWhen, timer, mergeMap, filter, switchMap, concat } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ObjectDetectionService } from '../../services/object-detection-api.service';
import { AsyncPipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-file-uploader',
  standalone: true,
  imports: [AsyncPipe, NgClass, FormsModule, TranslateModule],
  templateUrl: './file-uploader.component.html',
  styleUrl: './file-uploader.component.css',
})
export class FileUploaderComponent implements OnDestroy {
  acceptFormats: string = environment.acceptFormats;
  showErrorMessage = false;
  progress = 0;
  private subscription: Subscription | undefined;
  private http = inject(ObjectDetectionService);
  files = [];
  editing = false;
  imageUrl = '';
  showAlert = false;
  alertMessage = '';
  maxFilesCache = 0;

  /**
   * Unsubscribe from request
   */
  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }

  isLoading = false;
  /**
   * Reference to the file input element.
   */
  @ViewChild('fileInput', { read: ElementRef, static: false })
  fileInput!: ElementRef<HTMLInputElement>;

  /**
   * Event emitter that notifies when files are uploaded.
   */
  @Output() fileUploaded = new EventEmitter<any>();

  cd = inject(ChangeDetectorRef);

  /**
   * @public
   * @method onFileSelected
   * @description Handles the file selection event.
   * @param {Event} event - The event object containing the selected files.
   * @returns {void}
   */
  onFileSelected(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    this.prepareFilesList(files);
  }

  /**
   * Handles the drag-over event, preventing the default behavior.
   * @param event - The drag event object.
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  /**
   * Handles the drop event, preventing the default behavior and emitting the selected files.
   * @param event - The drop event object containing the dragged files.
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    this.prepareFilesList(files);
  }

  /**
   * Retrieves the status of a given operation, performing retries up to a maximum limit.
   *
   * @private
   * @param {string} operationId - The unique identifier for the operation whose status is to be fetched.
   * @param {number} [maxRetries=25] - The maximum number of retry attempts.
   * @returns {Observable<any>} - An Observable emitting the status of the operation.
   * @throws {Error} - Throws an error if the operation does not complete within the maximum allowed retries.
   *
   * @example
   * const observable = getStatusWithRetry("someOperationId", 5);
   * observable.subscribe(
   *   (status) => {
   *     // Handle successful operation status here
   *   },
   *   (error) => {
   *     // Handle error here
   *   }
   * );
   */
  private getStatusWithRetry(
    operationId: string,
    maxRetries = 50
  ): Observable<any> {
    let retryCount = 0;

    return this.http.GetStatus(operationId).pipe(
      expand((res) => {
        if (res.State === 'Completed') return [];

        if (retryCount >= maxRetries) {
          return throwError(
            () =>
              new Error(
                `Operation ${operationId} did not complete within ${maxRetries} attempts`
              )
          );
        }

        retryCount++;

        return this.http
          .GetStatus(res.OperationId)
          .pipe(delayWhen(() => timer(2000)));
      })
    );
  }

  /**
   * Enables editing mode.
   */
  enableEditing() {
    this.editing = true;
  }

  /**
   * Processes the observable stream representing a file upload operation.
   *
   * @private
   * @param {Observable<any>} uploadObservable - The observable stream representing the file upload.
   */
  private processFileUpload(uploadObservable: Observable<any>) {
    const images: any[] = [];
    this.showAlert = false;
    this.isLoading = true;

    uploadObservable
      .pipe(
        mergeMap((file) => this.getStatusWithRetry(file.Payload)),
        filter((res) => res.State === 'Completed')
      )
      .subscribe({
        next: (res: any) => {
          const { SharedURL, ImageSize, ImageName } = res.Payload.Pages[0];
          const [width, height] = ImageSize.split('x').map(Number);
          images.push({ src: SharedURL, width, height, name: ImageName });
        },
        error: () => {
          this.isLoading = false;
          this.showErrorMessage = true;
          this.cd.detectChanges();
        },
        complete: () => {
          this.isLoading = false;
          this.fileUploaded.emit(images[0]);
        },
      });
  }

  /**
   * Creates an observable stream for file upload based on a cloud storage service.
   *
   * @private
   * @param {any} service - The cloud storage service (e.g., Google, Dropbox).
   * @param {number} limit - The maximum number of files to be uploaded.
   * @returns {Observable<any>} - The observable stream for the file upload.
   */
  private createFileUploadStream(service: any, limit: number) {
    return service
      .uploadFromCloudStorage(limit)
      .pipe(
        switchMap((files: any[]) =>
          concat(
            ...files.map((file) => this.http.GetImagePreview(file.FileName))
          )
        )
      );
  }

  /**
   * Uploads files via Google cloud storage.
   */
  // uploadByGoogle() {
  //   const maxFiles = this.maxFiles;
  //   const googleUploadStream = this.createFileUploadStream(
  //     // this.googleService,
  //     maxFiles || 10
  //   );
  //   this.processFileUpload(googleUploadStream);
  // }

  /**
   * Uploads files via Dropbox cloud storage.
   */
  // uploadByDropbox() {
  //   const maxFiles = this.maxFiles;
  //   const dropboxUploadStream = this.createFileUploadStream(
  //     this.dropboxService,
  //     maxFiles || 10
  //   );
  //   this.processFileUpload(dropboxUploadStream);
  // }

  /**
   * Prepares a list of files for uploading.
   *
   * @param {FileList | null | undefined} files - The list of files to be prepared.
   */
  prepareFilesList(files: FileList | null | undefined) {
    if (!files) return;

    this.showAlert = false;

    const limitedFiles = Array.from(files);

    const nativeUploadStream = this.http
      .uploadFiles(limitedFiles)
      .pipe(
        switchMap((files) =>
          concat(
            ...files.map((file) => this.http.GetImagePreview(file.FileName))
          )
        )
      );

    this.processFileUpload(nativeUploadStream);
  }
}
